/**
 * The day's P&L for the drawer's tile — ROADMAP 6.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SERVER BRINGS THE JOURNAL, THIS BRINGS THE MARKS
 *
 * `GET /portfolio/day` returns what was held at the open and what has traded
 * since. It deliberately computes no P&L, because the two prices a day
 * figure needs are here and not there:
 *
 *   `ltp`   the current price
 *   `close` which DURING a session is the previous session's close — the
 *           baseline a carried position must be measured from, and the one
 *           the server cannot read mid-session without breaking the close
 *           cache's first rule
 *
 * Both are already in the tick store, live, per instrument.
 *
 * ON THE SAME CADENCE AS THE GREEKS, AND FOR THE SAME REASON
 *
 * The day total is a figure someone GLANCES at between decisions, not one
 * they trade off tick by tick. Recomputing it at feed rate would redraw the
 * header ten times a second with a number that never holds still, and would
 * re-fold every position's fills each time. `useTickLtps` keeps the
 * subscription to one hook whatever the leg count; the fold itself is
 * memoised on the marks.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useState } from 'react'
import { dayTotals, type DayLeg, type DayTotals, type FillEvent } from '@apna/shared'
import { api } from '@/lib/api'
import { tickStore } from '@/feed/tickStore'
import { useTickLtps } from '@/feed/useTick'

export interface DayJournalLeg {
  positionKey: string
  token: number
  tradingSymbol: string | null
  underlying: string | null
  strike: number | null
  optionType: string | null
  lotSize: number | null
  openingQty: number
  fills: FillEvent[]
}

export interface DayJournal {
  sessionDate: string
  legs: DayJournalLeg[]
}

export type DayState =
  | { readonly kind: 'LOADING' }
  /** The journal could not be read. NOT a day with no P&L in it. */
  | { readonly kind: 'UNREADABLE'; readonly why: string }
  | { readonly kind: 'READY'; readonly totals: DayTotals; readonly sessionDate: string }

/**
 * Attach the marks to one journal leg.
 *
 * A zero from the tick store is "nothing yet", never a price — the store
 * zero-initialises every slot, and `applySnapshot` refuses to write a zero
 * over anything. Both marks are passed as null in that case so `dayTotals`
 * can refuse the leg rather than measure it from nothing.
 */
export function markLeg(leg: DayJournalLeg, ltp: number | undefined): DayLeg {
  const slot = tickStore.slotFor(leg.token)
  const prevClose = tickStore.field(slot, 'close')
  return {
    key: leg.positionKey,
    openingQty: leg.openingQty,
    prevClose: prevClose > 0 ? prevClose : null,
    fills: leg.fills,
    ltp: ltp !== undefined && ltp > 0 ? ltp : null,
  }
}

/**
 * Validate the payload at the boundary.
 *
 * `api.get<DayJournal>` is a type ASSERTION, not a check — the response is
 * whatever the wire delivered, and a shape mismatch here reaches `.map` and
 * takes the whole drawer down with it, positions and orders included. A
 * header tile must never be able to do that.
 *
 * Returns null for anything that is not a journal, which the caller reports
 * as UNREADABLE — honest, and distinct from a day with no P&L in it.
 */
function asJournal(value: unknown): DayJournal | null {
  if (typeof value !== 'object' || value === null) return null
  const v = value as Partial<DayJournal>
  if (!Array.isArray(v.legs)) return null
  if (typeof v.sessionDate !== 'string') return null
  return {
    sessionDate: v.sessionDate,
    legs: v.legs.filter(
      (l): l is DayJournalLeg =>
        typeof l === 'object' &&
        l !== null &&
        typeof (l as DayJournalLeg).token === 'number' &&
        typeof (l as DayJournalLeg).positionKey === 'string' &&
        typeof (l as DayJournalLeg).openingQty === 'number' &&
        Array.isArray((l as DayJournalLeg).fills),
    ),
  }
}

/**
 * The day, across EVERY account.
 *
 * No `connectionId`: the drawer below this tile lists trades from all of
 * them, and a total covering only the account selected for execution would
 * not be the day. `GET /portfolio/day` with no account reads them all.
 */
export function useDayTotals(refreshToken: number): DayState {
  const [journal, setJournal] = useState<DayJournal | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    void api
      .get<unknown>('/portfolio/day')
      .then((r) => {
        if (cancelled) return
        const parsed = asJournal(r)
        if (parsed === null) {
          setError('the day journal came back in a shape this build does not understand')
          setJournal(null)
          return
        }
        setJournal(parsed)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        // A failed request is not a flat day. The tile must not read zero.
        setError(e instanceof Error ? e.message : 'could not read the day journal')
        setJournal(null)
      })
    return () => {
      cancelled = true
    }
  }, [refreshToken])

  const tokens = useMemo(() => (journal?.legs ?? []).map((l) => l.token), [journal])
  const ltps = useTickLtps(tokens)

  return useMemo((): DayState => {
    if (error !== null) return { kind: 'UNREADABLE', why: error }
    // Never a zero while the answer is still on its way: a tile that reads
    // +₹0 and then jumps is worse than one that says it is loading.
    if (journal === null) return { kind: 'LOADING' }
    return {
      kind: 'READY',
      sessionDate: journal.sessionDate,
      totals: dayTotals(journal.legs.map((l) => markLeg(l, ltps.get(l.token)))),
    }
  }, [error, journal, ltps])
}
